import React, {ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {Group, Image as KonvaImage, Layer, Line, Rect, Stage, Text as KonvaText, Transformer} from "react-konva";
import useResizeObserver from "use-resize-observer";
import {CanvasElement, GroupCanvasElement, ImageCanvasElement, ShapeCanvasElement, TextCanvasElement} from "./types";
import {colorBlue, gray, mediumGray} from "../../shared/colors";
import Konva from "konva";
import {IconButton, Typography} from "@mui/material";
import {ZoomInOutlined, ZoomOutOutlined} from "@mui/icons-material";
import {PD_SM} from "../../shared/dimens";
import {SHAPES_METADATA} from "./model/shapes/types";
import useImage from "use-image";

export const PAGE_WIDTH = 800;
export const PAGE_HEIGHT = 600;

const CANVAS_PADDING = 48;
const SNAP_THRESHOLD = 8; // pixels in page-space
const GUIDE_COLOR = colorBlue;
const GUIDE_DASH = [4, 4];

type SnapGuides = {
  vertical: number[];   // x positions in page-space
  horizontal: number[]; // y positions in page-space
}

function getElementEdges(el: CanvasElement): { vLines: number[], hLines: number[] } {
  return {
    vLines: [el.x, el.x + el.width / 2, el.x + el.width],
    hLines: [el.y, el.y + el.height / 2, el.y + el.height],
  };
}

function computeSnapGuides(
  dragged: { x: number, y: number, width: number, height: number },
  others: CanvasElement[],
): { guides: SnapGuides, snapDx: number, snapDy: number } {
  const dragV = [dragged.x, dragged.x + dragged.width / 2, dragged.x + dragged.width];
  const dragH = [dragged.y, dragged.y + dragged.height / 2, dragged.y + dragged.height];

  // Snap targets: page edges + center, plus all other elements' edges + centers
  const targetV = [0, PAGE_WIDTH / 2, PAGE_WIDTH];
  const targetH = [0, PAGE_HEIGHT / 2, PAGE_HEIGHT];
  for (const el of others) {
    const edges = getElementEdges(el);
    targetV.push(...edges.vLines);
    targetH.push(...edges.hLines);
  }

  let snapDx = 0;
  let snapDy = 0;
  const vertical: number[] = [];
  const horizontal: number[] = [];

  // Find closest vertical snap
  let bestDistV = SNAP_THRESHOLD + 1;
  for (const dv of dragV) {
    for (const tv of targetV) {
      const dist = Math.abs(dv - tv);
      if (dist < bestDistV) {
        bestDistV = dist;
        snapDx = tv - dv;
      }
    }
  }
  // Collect all matching vertical guides at snapped position
  if (bestDistV <= SNAP_THRESHOLD) {
    const snappedV = dragV.map(v => v + snapDx);
    for (const sv of snappedV) {
      for (const tv of targetV) {
        if (Math.abs(sv - tv) < 0.5) {
          vertical.push(tv);
        }
      }
    }
  } else {
    snapDx = 0;
  }

  // Find closest horizontal snap
  let bestDistH = SNAP_THRESHOLD + 1;
  for (const dh of dragH) {
    for (const th of targetH) {
      const dist = Math.abs(dh - th);
      if (dist < bestDistH) {
        bestDistH = dist;
        snapDy = th - dh;
      }
    }
  }
  if (bestDistH <= SNAP_THRESHOLD) {
    const snappedH = dragH.map(h => h + snapDy);
    for (const sh of snappedH) {
      for (const th of targetH) {
        if (Math.abs(sh - th) < 0.5) {
          horizontal.push(th);
        }
      }
    }
  } else {
    snapDy = 0;
  }

  return {
    guides: {vertical: [...new Set(vertical)], horizontal: [...new Set(horizontal)]},
    snapDx,
    snapDy,
  };
}

const SvgImage = React.forwardRef(function (
  {url, ...otherProps}: { url: string, [key: string]: any },
  ref: React.Ref<Konva.Image>,
) {
  const [image] = useImage(url);
  return <KonvaImage ref={ref} image={image} {...otherProps} />;
});

function renderChildElement(
  child: CanvasElement,
  offsetX: number,
  offsetY: number,
  scale: number,
): ReactElement | null {
  if (child.type === "image") {
    const imgEl = child as ImageCanvasElement;
    if (!imgEl.htmlImage) return null;
    return <KonvaImage
      key={child.id}
      image={imgEl.htmlImage}
      x={offsetX + child.x * scale}
      y={offsetY + child.y * scale}
      width={child.width * scale}
      height={child.height * scale}
      rotation={child.rotation}
      opacity={child.opacity}
    />;
  }
  if (child.type === "shape") {
    const shapeEl = child as ShapeCanvasElement;
    const url = SHAPES_METADATA.findMetadata(shapeEl.shape).url;
    return <SvgImage
      key={child.id}
      url={url}
      x={offsetX + child.x * scale}
      y={offsetY + child.y * scale}
      width={child.width * scale}
      height={child.height * scale}
      rotation={child.rotation}
      opacity={child.opacity}
    />;
  }
  return null;
}

type MarqueeRect = { x: number, y: number, width: number, height: number } | null;

function rectsIntersect(
  a: { x: number, y: number, width: number, height: number },
  b: { x: number, y: number, width: number, height: number },
): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

export function StudioCanvas(props: {
  elements: CanvasElement[];
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
  onElementUpdate: (id: string, x: number, y: number, width: number, height: number, rotation: number) => void;
  onElementTextUpdate?: (id: string, text: string) => void;
}): ReactElement {
  const {elements, selectedIds, onSelect, onElementUpdate} = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const {width = 0, height = 0} = useResizeObserver({ref: containerRef});
  const trRef = useRef<Konva.Transformer>(null);
  const nodesRef = useRef<Map<string, Konva.Node>>(new Map());

  const [zoomLevel, setZoomLevel] = useState(0); // 0 = fit-to-viewport
  const [guides, setGuides] = useState<SnapGuides>({vertical: [], horizontal: []});
  const [marquee, setMarquee] = useState<MarqueeRect>(null);
  const marqueeStartRef = useRef<{ x: number, y: number } | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const fitScale = width > 0 && height > 0
    ? Math.min((width - CANVAS_PADDING * 2) / PAGE_WIDTH, (height - CANVAS_PADDING * 2) / PAGE_HEIGHT)
    : 1;
  const scale = zoomLevel === 0 ? fitScale : zoomLevel;
  const zoomPercent = Math.round(scale * 100);
  const pageScaledWidth = PAGE_WIDTH * scale;
  const pageScaledHeight = PAGE_HEIGHT * scale;
  const pageX = (width - pageScaledWidth) / 2;
  const pageY = (height - pageScaledHeight) / 2;

  const ZOOM_STEP = 0.1;
  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 5;

  function handleZoomIn() {
    const current = zoomLevel === 0 ? fitScale : zoomLevel;
    setZoomLevel(Math.min(MAX_ZOOM, Math.round((current + ZOOM_STEP) * 10) / 10));
  }

  function handleZoomOut() {
    const current = zoomLevel === 0 ? fitScale : zoomLevel;
    setZoomLevel(Math.max(MIN_ZOOM, Math.round((current - ZOOM_STEP) * 10) / 10));
  }

  function handleZoomReset() {
    setZoomLevel(0);
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function handleWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }
      e.preventDefault();
      const current = zoomLevel === 0 ? fitScale : zoomLevel;
      const delta = -e.deltaY * 0.01;
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((current + delta) * 100) / 100));
      setZoomLevel(next);
    }

    container.addEventListener("wheel", handleWheel, {passive: false});
    return () => container.removeEventListener("wheel", handleWheel);
  }, [zoomLevel, fitScale]);

  useEffect(() => {
    if (!trRef.current) {
      return;
    }
    const nodes: Konva.Node[] = [];
    for (const id of selectedIds) {
      const el = elements.find(e => e.id === id);
      const node = nodesRef.current.get(id);
      if (node && el && !el.locked) {
        nodes.push(node);
      }
    }
    trRef.current.nodes(nodes);
    trRef.current.getLayer()?.batchDraw();
  }, [selectedIds, elements]);

  const setNodeRef = useCallback((id: string, node: Konva.Node | null) => {
    if (node) {
      nodesRef.current.set(id, node);
    } else {
      nodesRef.current.delete(id);
    }
  }, []);

  return (
    <div style={{position: "relative", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
      <div
        ref={containerRef}
        style={{flexGrow: 1, overflow: "auto", background: gray, position: "relative"}}
      >
        {width > 0 && height > 0 && (
          // @ts-ignore
          <Stage
            width={width}
            height={height}
            onMouseDown={(e) => {
              const target = e.target;
              if (target === target.getStage() || target.name() === "page-bg") {
                onSelect([]);
                const stage = target.getStage();
                const pos = stage?.getPointerPosition();
                if (pos) {
                  marqueeStartRef.current = {x: pos.x, y: pos.y};
                }
              }
            }}
            onMouseMove={(e) => {
              if (!marqueeStartRef.current) return;
              const stage = e.target.getStage();
              const pos = stage?.getPointerPosition();
              if (!pos) return;
              const start = marqueeStartRef.current;
              setMarquee({
                x: Math.min(start.x, pos.x),
                y: Math.min(start.y, pos.y),
                width: Math.abs(pos.x - start.x),
                height: Math.abs(pos.y - start.y),
              });
            }}
            onMouseUp={() => {
              if (marquee && marquee.width > 3 && marquee.height > 3) {
                // Convert marquee from screen to page-space and find intersecting elements
                const mPageX = (marquee.x - pageX) / scale;
                const mPageY = (marquee.y - pageY) / scale;
                const mPageW = marquee.width / scale;
                const mPageH = marquee.height / scale;
                const mRect = {x: mPageX, y: mPageY, width: mPageW, height: mPageH};
                const hitIds = elements
                  .filter(el => el.visible && !el.locked && !el.id.startsWith("bg_") && rectsIntersect(mRect, el))
                  .map(el => el.id);
                if (hitIds.length > 0) {
                  onSelect(hitIds);
                }
              }
              marqueeStartRef.current = null;
              setMarquee(null);
            }}>
            <Layer>
              <Rect
                name="page-bg"
                x={pageX}
                y={pageY}
                width={pageScaledWidth}
                height={pageScaledHeight}
                fill="white"
                shadowColor="rgba(0,0,0,0.18)"
                shadowBlur={20}
                shadowOffsetY={4}
                shadowEnabled
              />
              {elements.map(el => {
                if (!el.visible) {
                  return null;
                }
                const isBackground = el.id.startsWith("bg_");
                if (isBackground) {
                  // Background rendered as non-interactive image
                  if (el.type === "image") {
                    const imgEl = el as ImageCanvasElement;
                    if (!imgEl.htmlImage) return null;
                    return <KonvaImage
                      key={el.id}
                      image={imgEl.htmlImage}
                      x={pageX + el.x * scale}
                      y={pageY + el.y * scale}
                      width={el.width * scale}
                      height={el.height * scale}
                      listening={false}
                    />;
                  }
                  return null;
                }
                const otherElements = elements.filter(o => o.id !== el.id && o.visible && !o.id.startsWith("bg_"));
                const commonProps = {
                  key: el.id,
                  ref: (node: Konva.Node | null) => setNodeRef(el.id, node),
                  rotation: el.rotation,
                  opacity: el.opacity,
                  draggable: !el.locked,
                  onClick: () => onSelect([el.id]),
                  onTap: () => onSelect([el.id]),
                  onDragMove: (e: Konva.KonvaEventObject<DragEvent>) => {
                    const node = e.target;
                    const pageSpaceX = (node.x() - pageX) / scale;
                    const pageSpaceY = (node.y() - pageY) / scale;
                    const {guides: g, snapDx, snapDy} = computeSnapGuides(
                      {x: pageSpaceX, y: pageSpaceY, width: el.width, height: el.height},
                      otherElements,
                    );
                    if (snapDx !== 0) {
                      node.x(node.x() + snapDx * scale);
                    }
                    if (snapDy !== 0) {
                      node.y(node.y() + snapDy * scale);
                    }
                    setGuides(g);
                  },
                  onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
                    setGuides({vertical: [], horizontal: []});
                    onElementUpdate(
                      el.id,
                      (e.target.x() - pageX) / scale,
                      (e.target.y() - pageY) / scale,
                      el.width,
                      el.height,
                      el.rotation,
                    );
                  },
                  onTransform: (e: Konva.KonvaEventObject<Event>) => {
                    const node = e.target;
                    const sx = node.scaleX();
                    const sy = node.scaleY();
                    const w = Math.max(5, node.width() * sx) / scale;
                    const h = Math.max(5, node.height() * sy) / scale;
                    const px = (node.x() - pageX) / scale;
                    const py = (node.y() - pageY) / scale;
                    const {guides: g, snapDx, snapDy} = computeSnapGuides(
                      {x: px, y: py, width: w, height: h},
                      otherElements,
                    );
                    setGuides(g);
                    if (snapDx !== 0) {
                      node.x(node.x() + snapDx * scale);
                    }
                    if (snapDy !== 0) {
                      node.y(node.y() + snapDy * scale);
                    }
                  },
                  onTransformEnd: (e: Konva.KonvaEventObject<Event>) => {
                    setGuides({vertical: [], horizontal: []});
                    const node = e.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    node.scaleX(1);
                    node.scaleY(1);
                    onElementUpdate(
                      el.id,
                      (node.x() - pageX) / scale,
                      (node.y() - pageY) / scale,
                      Math.max(5, node.width() * scaleX) / scale,
                      Math.max(5, node.height() * scaleY) / scale,
                      node.rotation(),
                    );
                  },
                };
                if (el.type === "image") {
                  const imgEl = el as ImageCanvasElement;
                  if (!imgEl.htmlImage) {
                    return null;
                  }
                  return <KonvaImage
                    {...commonProps}
                    image={imgEl.htmlImage}
                    x={pageX + el.x * scale}
                    y={pageY + el.y * scale}
                    width={el.width * scale}
                    height={el.height * scale}
                  />;
                }
                if (el.type === "shape") {
                  const shapeEl = el as ShapeCanvasElement;
                  const sx = pageX + el.x * scale;
                  const sy = pageY + el.y * scale;
                  const sw = el.width * scale;
                  const sh = el.height * scale;
                  const url = SHAPES_METADATA.findMetadata(shapeEl.shape).url;
                  return <SvgImage
                    url={url}
                    {...commonProps}
                    x={sx}
                    y={sy}
                    width={sw}
                    height={sh}
                  />;
                }
                if (el.type === "group") {
                  const groupEl = el as GroupCanvasElement;
                  const gx = pageX + el.x * scale;
                  const gy = pageY + el.y * scale;
                  return <Group
                    {...commonProps}
                    x={gx}
                    y={gy}
                    width={el.width * scale}
                    height={el.height * scale}
                  >
                    {groupEl.children.map(child =>
                      renderChildElement(child, 0, 0, scale)
                    )}
                  </Group>;
                }
                if (el.type === "text") {
                  const textEl = el as TextCanvasElement;
                  const isEditing = editingTextId === el.id;
                  return <KonvaText
                    {...commonProps}
                    text={textEl.text}
                    x={pageX + el.x * scale}
                    y={pageY + el.y * scale}
                    width={el.width * scale}
                    fontSize={textEl.fontSize * scale}
                    fontFamily={textEl.fontFamily}
                    fontStyle={textEl.fontStyle}
                    fill={textEl.fill}
                    align={textEl.align}
                    visible={!isEditing}
                    onDblClick={() => setEditingTextId(el.id)}
                    onDblTap={() => setEditingTextId(el.id)}
                  />;
                }
                return null;
              })}
              <Transformer
                ref={trRef}
                boundBoxFunc={(oldBox: Konva.Box, newBox: Konva.Box) => {
                  if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                    return oldBox;
                  }
                  return newBox;
                }}
              />
              {guides.vertical.map((gx, i) => (
                <Line
                  key={`gv_${i}`}
                  points={[pageX + gx * scale, 0, pageX + gx * scale, height]}
                  stroke={GUIDE_COLOR}
                  strokeWidth={1}
                  dash={GUIDE_DASH}
                  listening={false}
                />
              ))}
              {guides.horizontal.map((gy, i) => (
                <Line
                  key={`gh_${i}`}
                  points={[0, pageY + gy * scale, width, pageY + gy * scale]}
                  stroke={GUIDE_COLOR}
                  strokeWidth={1}
                  dash={GUIDE_DASH}
                  listening={false}
                />
              ))}
              {marquee && (
                <Rect
                  x={marquee.x}
                  y={marquee.y}
                  width={marquee.width}
                  height={marquee.height}
                  fill="rgba(0, 102, 255, 0.1)"
                  stroke="#0066ff"
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
            </Layer>
          </Stage>
        )}
        {editingTextId && (() => {
          const el = elements.find(e => e.id === editingTextId) as TextCanvasElement | undefined;
          if (!el) return null;
          return <textarea
            autoFocus
            defaultValue={el.text}
            style={{
              position: "absolute",
              left: pageX + el.x * scale,
              top: pageY + el.y * scale,
              width: el.width * scale,
              minHeight: el.fontSize * scale * 1.5,
              fontSize: el.fontSize * scale,
              fontFamily: el.fontFamily,
              fontWeight: el.fontStyle.includes("bold") ? "bold" : "normal",
              fontStyle: el.fontStyle.includes("italic") ? "italic" : "normal",
              color: el.fill,
              textAlign: el.align,
              border: `2px solid ${colorBlue}`,
              borderRadius: 2,
              padding: 0,
              margin: 0,
              background: "white",
              outline: "none",
              resize: "none",
              overflow: "hidden",
              lineHeight: "1.2",
              zIndex: 10,
            }}
            onBlur={(e) => {
              props.onElementTextUpdate?.(editingTextId, e.target.value);
              setEditingTextId(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                props.onElementTextUpdate?.(editingTextId, (e.target as HTMLTextAreaElement).value);
                setEditingTextId(null);
              }
            }}
          />;
        })()}
      </div>
      <div style={{
        position: "absolute",
        bottom: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: PD_SM,
        padding: `${PD_SM}px 0`,
        background: gray,
      }}>
        <IconButton size="small" onClick={handleZoomOut} disabled={zoomPercent <= Math.round(MIN_ZOOM * 100)}>
          <ZoomOutOutlined fontSize="small"/>
        </IconButton>
        <Typography
          variant="body2"
          onClick={handleZoomReset}
          style={{
            cursor: "pointer",
            minWidth: 48,
            textAlign: "center",
            color: mediumGray,
            userSelect: "none",
          }}>
          {zoomPercent}%
        </Typography>
        <IconButton size="small" onClick={handleZoomIn} disabled={zoomPercent >= Math.round(MAX_ZOOM * 100)}>
          <ZoomInOutlined fontSize="small"/>
        </IconButton>
      </div>
    </div>
  );
}
